import {
  useState,
  useRef,
  useEffect,
  useCallback,
  type ReactNode,
} from 'react';
import { useTranslation } from 'react-i18next';
import { CalendarIcon, UserIcon, RepeatIcon, ClockIcon } from 'lucide-react';

import {
  Tabs,
  TabsContent,
  TabsList,
  TabsTrigger,
} from '@gokrew/ui/components/tabs';
import { Button, Chip } from '@gokrew/ui';
import { Tone } from '@gokrew/models/ui.types';
import { formatDate } from '@gokrew/ui/utility/utility';
import { TaskSourceTag } from '@gokrew/ui/components/tasks/TaskSourceTag';
import { TaskStatusChip } from '@gokrew/ui/components/tasks/TaskStatusChip';
import { TaskPriorityChip } from '@gokrew/ui/components/tasks/TaskPriorityChip';
import { TaskActivityTimeline } from '@gokrew/ui/components/tasks/TaskActivityTimeline';
import { TaskCommunicationPanel } from '@gokrew/ui/components/tasks/TaskCommunicationPanel';
import { AttachmentRow } from '@gokrew/ui/components/tasks/AttachmentRow';
import { TaskDetailTimestamps } from '@gokrew/ui/components/tasks/TaskDetailTimestamps';
import { TaskLockedBanner } from '@gokrew/ui/components/tasks/TaskLockedBanner';
import {
  getNextTaskStatusAction,
  buildTaskRecurrenceSummary,
} from '@gokrew/ui/utility/tasks';
import { useStagedAttachments } from '@gokrew/ui/hooks';
import { useDialog } from '@gokrew/ui/context';

import {
  isSendStep,
  type TaskAttachment,
  type ChangeMyStatusDto,
} from '@gokrew/models/tasks';
import { useNavigate } from 'react-router-dom';
import styles from './EmployeeTaskDetailDialog.module.css';

import {
  useEditEmployeeTaskMessage,
  useGetEmployeeTaskThreadMessages,
  useGetMyEmployeeTaskById,
  usePostEmployeeTaskMessage,
  useUpdateMyEmployeeTaskStatus,
  useDeleteEmployeeTaskMessage,
  useGetOrCreateEmployeeTaskThread,
  useGetEmployeeTaskActivity,
} from '@/hooks/UseEmployeeTasks';
import { useUploadDocument } from '@/hooks/useUploadDocument';
import { QueryKeys, TranslationNS } from '@gokrew/models/constants';
import i18n from '@/i18n';
import { useEmployeePortalAuth } from '@/context/EmployeeAuthContext';

// ─── Sub-components ───────────────────────────────────────────
function MetaRow({ label, children }: { label: string; children: ReactNode }) {
  return (
    <div>
      <p className="mb-1 text-[11px] font-semibold uppercase tracking-wider text-gray-400">
        {label}
      </p>
      <div className="text-sm text-gray-700">{children}</div>
    </div>
  );
}

// ─── Main dialog ──────────────────────────────────────────────
interface Props {
  assignmentId: string;
}

export function EmployeeTaskDetailDialog({ assignmentId }: Props) {
  const { t } = useTranslation(TranslationNS.EmployeeTasks);
  const navigate = useNavigate();
  const { closeAll } = useDialog();
  const [activeThreadId, setActiveThreadId] = useState<string | undefined>();
  const attemptedCreateRef = useRef<string | null>(null);
  const { currentUser, user } = useEmployeePortalAuth();

  const { mutate: editMsg } = useEditEmployeeTaskMessage();
  const { mutate: deleteMsg } = useDeleteEmployeeTaskMessage();

  const { data, isLoading } = useGetMyEmployeeTaskById(assignmentId);
  const { data: rawActivities } = useGetEmployeeTaskActivity(assignmentId);
  const activities = Array.isArray(rawActivities) ? rawActivities : [];

  const { mutateAsync: postMessage, isPending: isSending } =
    usePostEmployeeTaskMessage();
  const { mutate: updateStatus, isPending: isUpdatingStatus } =
    useUpdateMyEmployeeTaskStatus();
  const { mutate: getOrCreateThread } = useGetOrCreateEmployeeTaskThread();

  const resolvedCompanyId = data?.task?.companyId ?? 'pending';

  const { uploadDocument } = useUploadDocument({
    destinationPath: `assets/companies/${resolvedCompanyId}/task-messages`,
    onUpdate: async () => {},
    invalidateKey: [QueryKeys.FileUpload],
    showToast: false,
  });

  // Determine active thread — prefer group thread, fall back to first.
  // Flow tasks have no thread until first use: lazily get-or-create it once
  // per assignment instead of leaving the Communication tab permanently disabled.
  useEffect(() => {
    if (!data) return;
    const group = data.threads?.find((t) => t.type === 'group');
    const existing =
      group?.id ?? data.assignment?.threadId ?? data.threads?.[0]?.id;
    if (existing) {
      setActiveThreadId(existing);
      return;
    }
    if (attemptedCreateRef.current === assignmentId) return;
    attemptedCreateRef.current = assignmentId;
    getOrCreateThread(assignmentId, {
      onSuccess: (res) => setActiveThreadId(res.data.data.id),
    });
  }, [data, assignmentId, getOrCreateThread]);

  const threadId =
    activeThreadId ?? data?.assignment?.threadId ?? data?.threads?.[0]?.id;
  const { data: messages = [] } = useGetEmployeeTaskThreadMessages(threadId);

  // This mutation needs a nested `payload.body` (unlike TaskCommunicationPanel's
  // flat `{threadId, messageId, body}`) — a thin adapter, stable via useCallback.
  const updateMessage = useCallback(
    (
      args: { threadId: string; messageId: string; body: string },
      options: { onSuccess: () => void }
    ) =>
      editMsg(
        {
          threadId: args.threadId,
          messageId: args.messageId,
          payload: { body: args.body },
        },
        options
      ),
    [editMsg]
  );

  const submitAttachments = useStagedAttachments(uploadDocument);

  // ── Handlers ─────────────────────────────────────────────

  const handleStatusUpdate = (status: ChangeMyStatusDto['status']) => {
    updateStatus(
      {
        assignmentId,
        payload: {
          status,
          ...(submitAttachments.readyAttachments.length
            ? { attachments: submitAttachments.readyAttachments }
            : {}),
        },
      },
      {
        onSuccess: () => submitAttachments.reset(),
      }
    );
  };

  // ── Render guards ─────────────────────────────────────────

  if (isLoading) {
    return (
      <div className="flex h-48 items-center justify-center">
        <div className="h-6 w-6 animate-spin rounded-full border-2 border-indigo-600 border-t-transparent" />
      </div>
    );
  }

  if (!data || !data.assignment) {
    return (
      <div className="py-10 text-center text-sm text-red-500">
        {t('tasks.failedLoad')}
      </div>
    );
  }

  const {
    task,
    assignment,
    occurrence,
    source,
    flowType,
    flowStatus,
    flowEmployee,
    assignedBy,
    threads,
  } = data;
  const nextAction = getNextTaskStatusAction(
    assignment.status,
    occurrence.requiresApproval,
    flowStatus === 'completed'
  );
  const isLocked = !!task.dependentTaskId && !task.unlockedAt;
  // A workflow step that sent a document (D68) is done by signing it or
  // confirming it as read, not by a status button.
  const sendStep = isSendStep(task.stepKind);
  const documentPath =
    task.stepKind === 'send_for_signature'
      ? data.stepDocument?.signatureRequestId
        ? `/signatures?request=${data.stepDocument.signatureRequestId}`
        : '/signatures'
      : data.stepDocument?.acknowledgementId
        ? `/signatures/read/${data.stepDocument.acknowledgementId}`
        : '/signatures';
  // The occurrence's own assignmentMode snapshot decides this, not the task's
  // current mode — an occurrence keeps the thread shape it was fanned out with
  // even after the task is later switched to a different mode.
  const isGroupTask =
    (occurrence.assignmentMode ?? task.assignmentMode) === 'group';
  // flowEmployee is the subject of the onboarding/offboarding flow this task
  const isOwnFlowTask = !flowEmployee || flowEmployee.id === user?.id;
  const nextActionStyle =
    nextAction?.value === 'submitted' || nextAction?.value === 'completed'
      ? '!bg-green-600 !hover:bg-green-700 !border-green-600'
      : nextAction?.value === 'in_progress' && assignment.status === 'completed'
        ? '!bg-amber-500 !hover:bg-amber-600 !border-amber-500'
        : '';

  const recurSummary = buildTaskRecurrenceSummary(
    task.recurrenceRule,
    t,
    i18n.language
  );

  return (
    <div className="flex flex-col gap-5 bg-white">
      {/* ── Header ── */}
      <div className="flex items-start justify-between gap-4 border-b border-gray-100 pb-4">
        <div className="min-w-0">
          <div className="flex flex-col gap-3">
            <h2 className="text-lg font-semibold ml-1 text-gray-900 leading-snug wrap-break-word">
              {occurrence.title}
            </h2>
            <div className="flex flex-wrap items-center gap-2">
              <TaskPriorityChip priority={occurrence.priority} />
              <TaskStatusChip status={assignment.status} />
              <TaskSourceTag source={source} flowType={flowType} />
              {task.scheduleType === 'recurring' && (
                <Chip type={Tone.ACCENT} size="xs">
                  <RepeatIcon size={9} />
                  {t('dialog.recurring')}
                </Chip>
              )}
            </div>
          </div>

          {!isOwnFlowTask && flowEmployee?.name && (
            <div className="mt-2 inline-flex items-center gap-1.5 rounded-md border border-gray-100 bg-gray-50 px-2 py-1 text-xs text-gray-600">
              <UserIcon size={11} className="shrink-0 text-gray-400" />
              {t('dialog.forEmployee', { name: flowEmployee.name })}
            </div>
          )}
        </div>

        {sendStep && !isLocked && assignment.status !== 'completed' && (
          <Button
            variant="primary"
            size="sm"
            onClick={() => {
              // Land on the document itself: this dialog would otherwise
              // stay open on top of the viewer.
              closeAll();
              navigate(documentPath);
            }}
            className="shrink-0"
          >
            {t(
              task.stepKind === 'send_for_signature'
                ? 'dialog.openToSign'
                : 'dialog.openToRead'
            )}
          </Button>
        )}

        {nextAction && !isLocked && !sendStep && (
          <Button
            variant="primary"
            size="sm"
            disabled={isUpdatingStatus || submitAttachments.hasUploading}
            onClick={() => handleStatusUpdate(nextAction.value)}
            className={`shrink-0 ${nextActionStyle}`}
          >
            {isUpdatingStatus ? t('dialog.saving') : t(nextAction.labelKey)}
          </Button>
        )}
      </div>

      {/* ── Locked banner ── */}
      {isLocked && <TaskLockedBanner message={t('dialog.lockedMessage')} />}
      {sendStep && !isLocked && (
        <p className={styles.stepNotice}>
          {t(
            assignment.status === 'completed'
              ? 'dialog.stepDone'
              : task.stepKind === 'send_for_signature'
                ? 'dialog.stepWaitingSignature'
                : 'dialog.stepWaitingRead'
          )}
        </p>
      )}

      {/* ── Tabs ── */}
      <Tabs defaultValue="details">
        <TabsList>
          <TabsTrigger value="details">{t('dialog.details')}</TabsTrigger>
          <TabsTrigger value="communication" disabled={!threadId}>
            {isGroupTask
              ? t('dialog.groupCommunication')
              : t('dialog.communication')}
            {messages.length > 0 && (
              <span className="ml-1.5 rounded-full bg-indigo-100 px-1.5 py-0.5 text-[10px] font-semibold text-indigo-700">
                {messages.length}
              </span>
            )}
          </TabsTrigger>
          <TabsTrigger value="activity">{t('dialog.activity')}</TabsTrigger>
        </TabsList>

        {/* ── Details Tab ── */}
        <TabsContent value="details" className="space-y-5">
          {occurrence.description && (
            <div>
              <p className="mb-1.5 text-[11px] font-semibold uppercase tracking-wider text-gray-400">
                {t('dialog.description')}
              </p>
              <p className="text-sm leading-relaxed text-gray-600 wrap-break-word">
                {occurrence.description}
              </p>
            </div>
          )}

          <div className="grid grid-cols-2 gap-x-6 gap-y-4">
            <MetaRow label={t('dialog.assignDate')}>
              <div className="flex items-center gap-1.5">
                <CalendarIcon size={13} className="text-gray-400" />
                {occurrence?.assignDate
                  ? formatDate(occurrence.assignDate, {
                      format: 'medium',
                      locale: i18n.language,
                    })
                  : '—'}
              </div>
            </MetaRow>

            <MetaRow label={t('dialog.dueDate')}>
              <div className="flex items-center gap-1.5">
                <CalendarIcon size={13} className="text-gray-400" />
                {occurrence?.dueDate
                  ? formatDate(occurrence.dueDate, {
                      format: 'medium',
                      locale: i18n.language,
                    })
                  : '—'}
              </div>
            </MetaRow>

            <MetaRow label={t('dialog.assignedBy')}>
              <div className="flex min-w-0 items-center gap-1.5">
                <UserIcon size={13} className="shrink-0 text-gray-400" />
                <span className="min-w-0 truncate">
                  {assignedBy?.name ?? '—'}
                </span>
                {assignedBy?.role && (
                  <span className="shrink-0 text-xs capitalize text-gray-400">
                    ({assignedBy.role})
                  </span>
                )}
              </div>
            </MetaRow>

            <MetaRow label={t('dialog.schedule')}>
              <div className="flex items-center gap-1.5">
                <ClockIcon size={13} className="text-gray-400" />
                <span>
                  {task.scheduleType === 'one_time'
                    ? t('dialog.oneTime')
                    : t('dialog.recurring')}
                </span>
              </div>
            </MetaRow>
          </div>

          {recurSummary && (
            <div className="flex items-center gap-2 rounded-lg border border-indigo-100 bg-indigo-50 px-3 py-2">
              <RepeatIcon size={13} className="shrink-0 text-indigo-400" />
              <span className="text-xs text-indigo-700">{recurSummary}</span>
            </div>
          )}

          <TaskDetailTimestamps
            startedAt={assignment.startedAt}
            submittedAt={assignment.submittedAt}
            completedAt={assignment.completedAt}
            approvedAt={assignment.approvedAt}
          />

          {assignment.status === 'completed' && assignment.completedAt && (
            <div className="rounded-lg border border-green-200 bg-green-50 px-4 py-3">
              <p className="text-sm text-green-800">
                {t('dialog.completedOn', {
                  date: formatDate(assignment.completedAt, {
                    format: 'mediumTime',
                    locale: i18n.language,
                  }),
                })}
              </p>
            </div>
          )}

          {assignment.adminNote && (
            <div className="rounded-lg border border-amber-200 bg-amber-50 px-4 py-3">
              <p className="mb-1 text-[11px] font-semibold uppercase tracking-wider text-amber-700">
                {t('dialog.adminNote')}
              </p>
              <p className="text-sm text-amber-800 wrap-break-word">
                {assignment.adminNote}
              </p>
            </div>
          )}

          {assignment.submissionAttachments &&
            assignment.submissionAttachments.length > 0 && (
              <div>
                <p className="mb-2 text-[11px] font-semibold uppercase tracking-wider text-gray-400">
                  {t('dialog.submissionAttachments')}
                </p>
                <div className="space-y-1.5">
                  {assignment.submissionAttachments.map((att) => (
                    <AttachmentRow
                      key={att.id}
                      fileName={att.fileName}
                      fileUrl={att.fileUrl}
                      labels={{
                        fallback: t('dialog.attachment'),
                        view: t('dialog.view'),
                      }}
                    />
                  ))}
                </div>
              </div>
            )}

          {task.attachments?.length > 0 && (
            <div>
              <p className="mb-2 text-[11px] font-semibold uppercase tracking-wider text-gray-400">
                {t('dialog.taskAttachments')}
              </p>
              <div className="space-y-1.5">
                {task.attachments.map((att: TaskAttachment) => (
                  <AttachmentRow
                    key={att.id}
                    fileName={att.fileName}
                    fileUrl={att.fileUrl}
                    labels={{
                      fallback: t('dialog.attachment'),
                      view: t('dialog.view'),
                    }}
                  />
                ))}
              </div>
            </div>
          )}
        </TabsContent>

        {/* ── Communication Tab ── */}
        <TabsContent value="communication" className="max-h-[500px]">
          <TaskCommunicationPanel
            accent="violet"
            threadId={threadId}
            messages={messages}
            currentUserId={currentUser?.id}
            language={i18n.language}
            isSending={isSending}
            onSend={async (payload) => {
              if (!threadId) return;
              await postMessage({ threadId, payload });
            }}
            updateMessage={updateMessage}
            deleteMessage={deleteMsg}
            uploadFile={uploadDocument}
            threads={threads?.map((tItem) => ({
              id: tItem.id,
              label:
                tItem.type === 'group'
                  ? t('dialog.groupCommunication')
                  : t('dialog.communication'),
            }))}
            onThreadChange={setActiveThreadId}
          />
        </TabsContent>

        {/* ── Activity Tab ── */}
        <TabsContent value="activity">
          <TaskActivityTimeline activities={activities} />
        </TabsContent>
      </Tabs>
    </div>
  );
}
